import type { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from './AuthProvider'
import type { PermissionAction } from './types'
import { PageSpinner } from '@/shared/ui/Brand'

/**
 * Route guards are UX, not security: the server re-checks every permission on
 * every call. Their job is to avoid rendering a screen the user will only be
 * refused from, and to send them somewhere useful instead of a blank page.
 */

export function RequireAuth({ children }: { children: ReactNode }) {
  const { user, initialising } = useAuth()
  const location = useLocation()

  // Never redirect while the silent refresh is still in flight, or a reload on
  // a deep link would bounce a perfectly valid session to the login page.
  if (initialising) return <PageSpinner />

  if (!user) {
    // Remember where they were headed so login can return them there.
    return <Navigate to="/login" replace state={{ next: location.pathname + location.search }} />
  }

  return <>{children}</>
}

export function RequirePermission({
  permCode,
  action = 'view',
  children,
}: {
  permCode: string
  action?: PermissionAction
  children: ReactNode
}) {
  const { can, initialising } = useAuth()
  if (initialising) return <PageSpinner />
  if (!can(permCode, action)) return <Navigate to="/403" replace />
  return <>{children}</>
}

/**
 * Conditional rendering for a single control.
 *
 * Hides rather than disables: a greyed-out button the user can never enable is
 * an invitation to file a ticket.
 */
export function Can({
  permCode,
  action = 'view',
  children,
}: {
  permCode: string
  action?: PermissionAction
  children: ReactNode
}) {
  const { can } = useAuth()
  return can(permCode, action) ? <>{children}</> : null
}
